import {
	IconCalculator,
	IconDatabase,
	IconHouse,
	IconIntelligenceLayer,
	IconPlanning,
} from "@exopengithub/exokit";
import { SidebarToggleButton } from "@exopengithub/exokit/v2";
import { clsx } from "clsx";
import { useContext, type ReactElement } from "react";
import {
	IconCode,
	IconShield,
	IconBinaryTree,
	IconBuilding,
	IconChartBar,
} from "@tabler/icons-react";
import { useLocation, useParams } from "react-router";
import { useTranslation } from "react-i18next";

import { NavItem } from "./NavItem";
import { useUser } from "../../common/context/userContextUtils";
import { CompanySelect } from "./CompanySelect.js";
import { LegalEntitySelect } from "./LegalEntitySelect.js";
import { SidebarContext } from "./SidebarContext.js";

import { useEwrLinkClick } from "src/common/service/useEwrLinkClick.js";
import { useLoadedCompanyDomain } from "src/routes/routeDataHooks.js";
import { useDevToolsUnlocked } from "./useDevToolsUnlock";
import { getProduct } from "src/features/products/products.js";
import { ExperienceRunner } from "src/features/experiences/ExperienceRunner.js";
import { useProductAccess } from "src/features/products/useProductAccess.js";
import { useLanguageToggle } from "./useLanguageToggle.js";
import { DOMAIN_TERMS } from "src/common/domainTerms.js";

interface CompanySidebarPartProps {
	sidebarOpen: boolean;
}

export const CompanySidebarPart = ({
	sidebarOpen,
}: CompanySidebarPartProps) => {
	const companyDomain = useLoadedCompanyDomain();

	const user = useUser();
	const location = useLocation();
	const { legalEntityId } = useParams();
	const { toggleSidebar } = useContext(SidebarContext);

	const { t } = useTranslation();
	const devToolsUnlocked = useDevToolsUnlocked(user.userIsSuperAdmin());
	useLanguageToggle();

	const legalEntityBase = `/company-domains/${companyDomain.id}/legal-entities/${legalEntityId}`;
	const companiesListHref = `/company-domains/${companyDomain.id}/legal-entities`;
	// WHY not the list when a company is picked: it is domain scope, so it would ask to switch twice.
	const companiesHref = legalEntityId
		? `${legalEntityBase}/info`
		: companiesListHref;
	const scopeBase = legalEntityId
		? legalEntityBase
		: `/company-domains/${companyDomain.id}`;
	const featureSegment = location.pathname.startsWith(scopeBase)
		? (location.pathname.slice(scopeBase.length).split("/")[1] ?? "")
		: "";
	// Anchored to the scope base — a substring match would also hit e.g. /internal/dev-tools/planning.
	const isPlanningActive = ["planning", "plan"].includes(featureSegment);
	// Companies covers the list and every legal-entity detail sub-page, except Planning/Data sources, which have their own nav items.
	const legalEntitySegment = legalEntityId
		? (location.pathname.slice(legalEntityBase.length).split("/")[1] ?? "")
		: "";
	// The company's own root is Start, not Företag — both would light up otherwise.
	const isAtCompanyStart =
		Boolean(legalEntityId) && location.pathname === legalEntityBase;
	const isCompaniesActive =
		location.pathname.startsWith(companiesListHref) &&
		!isAtCompanyStart &&
		!["planning", "plan", "data-sources", "hierarchies"].includes(
			legalEntitySegment,
		);

	const { access: productAccess, excelTarget } = useProductAccess();
	const intelligenceLayer = getProduct("intelligenceLayer");
	const excel = getProduct("excel");
	const powerBi = getProduct("powerBi");

	const { onClick: handleEwrClick } = useEwrLinkClick(
		excelTarget?.kind === "ewr" ? excelTarget.url : "",
	);

	const productLinks: ReactElement[] = [];
	if (productAccess.intelligenceLayer.active)
		productLinks.push(
			<NavItem
				key="intelligence layer"
				to={intelligenceLayer.path(companyDomain)}
				label={intelligenceLayer.name}
				icon={<IconIntelligenceLayer />}
			/>,
		);

	if (excelTarget)
		productLinks.push(
			<NavItem
				key="report"
				to={
					excelTarget.kind === "reportPicker"
						? excelTarget.path
						: excelTarget.url
				}
				label={excel.name}
				icon={<IconCalculator />}
				onClick={excelTarget.kind === "ewr" ? handleEwrClick : undefined}
			/>,
		);

	if (productAccess.powerBi.active)
		productLinks.push(
			<NavItem
				key="powerbi reports"
				to={powerBi.path(companyDomain)}
				label={powerBi.name}
				icon={<IconChartBar />}
			/>,
		);

	return (
		<div className="grow overflow-y-auto">
			{/* WHY only when open: the pickers it points at are unmounted while collapsed, and the runner snapshots its anchors once. */}
			{sidebarOpen && <ExperienceRunner experienceId="company-picker-tour" />}
			{/* WHY always rendered, invisible when collapsed: reserves the exact
			 * same height as open (border/p-3/h-9 pickers), so every icon below
			 * stays at the same y regardless of open/collapsed. */}
			<div
				className={clsx(
					"mx-4 mb-2 flex flex-col gap-1 border border-[#00A329]/20 p-3",
					!sidebarOpen && "invisible",
				)}
			>
				<div className="rounded-xs bg-white">
					{sidebarOpen ? (
						<CompanySelect
							canCreateCompanyDomain={user.userIsSuperAdmin()}
							canSelectAllDomains={user.userIsSuperAdmin()}
						/>
					) : (
						<div className="h-9" />
					)}
				</div>
				{sidebarOpen ? (
					<div className="rounded-xs bg-white">
						<LegalEntitySelect />
					</div>
				) : (
					<div className="h-9" />
				)}
			</div>
			<div
				className={clsx(
					"relative mx-4 mb-1 flex items-center justify-between p-3",
					!sidebarOpen && "invisible",
				)}
			>
				<span className="text-xs font-medium text-[#00661A]/60">
					{t("Menu")}
				</span>
				{sidebarOpen && (
					<SidebarToggleButton
						onClick={toggleSidebar}
						label={t("Collapse sidebar")}
						iconClassName="text-primary-800"
					/>
				)}
				{/* WHY visible: overrides the row's own invisible above — the
				 * toggle takes this exact spot instead of adding a row of its
				 * own, so it never moves between open and collapsed. */}
				{!sidebarOpen && (
					<div className="visible absolute inset-0 flex items-center justify-center">
						<SidebarToggleButton
							onClick={toggleSidebar}
							label={t("Expand sidebar")}
							iconClassName="text-primary-800"
							className="rounded-xs p-3 hover:bg-white/60"
						/>
					</div>
				)}
			</div>
			<nav>
				<div className="mx-4 mb-4 space-y-1">
					<NavItem
						to={
							legalEntityId
								? legalEntityBase
								: `/company-domains/${companyDomain.id}`
						}
						label="Start"
						icon={<IconHouse />}
						end
					/>
					<NavItem
						to={companiesHref}
						label={t(DOMAIN_TERMS.COMPANIES)}
						icon={<IconBuilding />}
						isActiveOverride={isCompaniesActive}
					/>
					{productLinks}
					<NavItem
						to={
							legalEntityId
								? `${legalEntityBase}/planning`
								: `/company-domains/${companyDomain.id}/planning`
						}
						label="Planning"
						icon={<IconPlanning />}
						isActiveOverride={isPlanningActive}
					/>
					<NavItem
						label={t(DOMAIN_TERMS.DATA_SOURCES)}
						to={
							legalEntityId
								? `${legalEntityBase}/data-sources`
								: `/company-domains/${companyDomain.id}/data-sources`
						}
						icon={<IconDatabase />}
					/>
					{user.userIsSuperAdmin() && devToolsUnlocked && (
						<NavItem
							to={`/company-domains/${companyDomain.id}/permissions`}
							label={t("Permissions")}
							icon={<IconShield />}
						/>
					)}
					{devToolsUnlocked && (
						<NavItem
							to={
								legalEntityId
									? `${legalEntityBase}/hierarchies`
									: `/company-domains/${companyDomain.id}/hierarchies`
							}
							label={t("Hierarchies 2.0")}
							icon={<IconBinaryTree />}
							isActiveOverride={location.pathname.includes("/hierarchies")}
						/>
					)}
					{user.userIsSuperAdmin() && devToolsUnlocked && (
						<NavItem
							to={`/company-domains/${companyDomain.id}/internal/dev-tools`}
							label="Utvecklingsverktyg"
							icon={<IconCode />}
						/>
					)}
				</div>
			</nav>
		</div>
	);
};
